import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useState } from 'react'
import ClinicOrderStagePanel from './ClinicOrderStagePanel'
import EditLabOrderModal from './EditLabOrderModal'
import JobCardPanel from './JobCardPanel'
import LocalOrderItemsEditor from './LocalOrderItemsEditor'
import LocalOrderTrackingPanel from './LocalOrderTrackingPanel'
import type { LabOrderDetail } from './labOrderTypes'

type ClinicOrderPanelsProps = {
  order: LabOrderDetail
  onUpdated: (order: LabOrderDetail) => void
}

/** Fields only the clinic records; they never make the order "editable". */
const QUICK_FIELDS = ['patientInformed', 'labInvoiceNo', 'payment']

/**
 * What the clinic can do on a lab order: edit it (what the server allows
 * for its status), view / print / email the job card, log stages, and for
 * a self-tracked lab also set prices and record payments. Shown above the
 * order details on the Dental Labs orders page and the treatment's Lab order
 * tab.
 */
export default function ClinicOrderPanels({ order, onUpdated }: ClinicOrderPanelsProps) {
  const [editing, setEditing] = useState(false)
  const isLocal = order.isLocalTracking || order.trackingMode === 'local'
  const editable = order.editable
  const canEdit = !!editable && editable.fields.some((f) => !QUICK_FIELDS.includes(f))

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
        <p className="text-xs text-slate-500">
          {canEdit
            ? editable?.reason || 'You can change this order until the lab accepts it.'
            : editable?.reason || 'This order can no longer be changed.'}
        </p>
        <button
          type="button"
          onClick={() => setEditing(true)}
          disabled={!canEdit}
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <EditOutlinedIcon sx={{ fontSize: 16 }} />
          Edit order
        </button>
      </div>
      <JobCardPanel key={`card-${order._id}`} order={order} onUpdated={onUpdated} />
      <ClinicOrderStagePanel key={`stage-${order._id}`} order={order} onUpdated={onUpdated} />
      {isLocal ? (
        <>
          <LocalOrderItemsEditor key={`items-${order._id}`} order={order} onUpdated={onUpdated} />
          <LocalOrderTrackingPanel order={order} onUpdated={onUpdated} />
        </>
      ) : null}
      {editing ? (
        <EditLabOrderModal
          order={order}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setEditing(false)
            onUpdated(updated)
          }}
        />
      ) : null}
    </>
  )
}
